import { useSyncExternalStore } from 'react';

/**
 * IU-9 (F-0510, `docs/concept-iphone-ux.md#iu-9`, row 34 of *Every action and its rule*): the list
 * a pushed record was opened from, so the back row's ⌃ ⌄ can step to the next or previous record
 * without that list staying mounted (it never does — the pushed screen replaces it).
 *
 * A small store outside React, the same shape as `phoneChrome.tsx`'s own top-row slots: one list is
 * open at a time, so a global holder needs no provider every list root would otherwise have to
 * mount. Set by the row that opens a record — `lane-phone-rows` calls `setOriginatingList` once
 * this module is merged; nothing here reads the rows themselves, and nothing in this file is
 * reachable from a list that never called it.
 *
 * ⛔ **A cold open has none.** A shared link or a fresh tab lands on the record directly, with no
 * list ever having called `setOriginatingList` — `useOriginatingListStep` reads `null` then, and
 * the back row draws neither ⌃ nor ⌄ (S-4 *Target, a pushed screen*; row 34's own "absent on a cold
 * open, never disabled").
 */
/**
 * One record of that list: its key, and the address its own row opens it at.
 *
 * ⛔ **The address is the row's, never derived from the key** (I-1264, AC-18.12). A list in a scope
 * that spans work areas (All work areas, an organisation) addresses each record with its work area
 * in the path (`/-/work-areas/decisions/acme/tracker/D-0002`, I-0602), so `${list}/${key}` names no
 * page. The row already built the right address with its surface's own builder; the step reuses it.
 */
export interface IOriginatingEntry {
  readonly key: string;
  readonly href: string;
}

export interface IOriginatingList {
  /** The list's own records, in the order the reader saw them. */
  readonly entries: readonly IOriginatingEntry[];
  /** Which one was opened — kept for a caller that wants it; stepping itself re-finds the current
   *  record in `entries` rather than trusting this once the reader has moved (see `useOriginatingListStep`). */
  readonly index: number;
}

let current: IOriginatingList | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Called by the row that opens a record: the list it came from, and which position was tapped. */
export function setOriginatingList(entries: readonly IOriginatingEntry[], index: number): void {
  current = { entries: [...entries], index };
  notify();
}

/** Leaving every pushed record behind (back to a tab root, Settings, another work area) clears it,
 *  so a later cold-ish open (e.g. a deep link typed while the app is still running) reads none. */
export function clearOriginatingList(): void {
  if (current === null) return;
  current = null;
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): IOriginatingList | null {
  return current;
}

function serverSnapshot(): null {
  return null;
}

/** The raw store, for a caller that wants the whole list (a test's stub, mostly). */
export function useOriginatingList(): IOriginatingList | null {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

export interface IOriginatingListStep {
  readonly prev: IOriginatingEntry | null;
  readonly next: IOriginatingEntry | null;
}

/** An address without its query or fragment, decoded, so two spellings of one path compare equal. */
function pathOf(href: string): string {
  const path = href.split(/[?#]/, 1)[0];
  try {
    return decodeURI(path);
  } catch {
    return path;
  }
}

/**
 * Where the record at `currentPath` sits in `entries`, found by its address as ⌃ ⌄ find it; -1 when
 * no row is at it. F-0567 (NX-7): the next waiting decision follows the same list.
 */
export function originatingIndexOf(
  entries: readonly IOriginatingEntry[],
  currentPath: string,
): number {
  const here = pathOf(currentPath);
  return entries.findIndex((entry) => pathOf(entry.href) === here);
}

/**
 * ⌃ ⌄'s own targets for the record on screen: the adjacent records in the list it was opened from,
 * or `null` with no list at all. The current record is looked up fresh every call, never trusted to
 * still sit at the stored `index` — stepping to the next record changes which one is "current"
 * without the list itself telling this store again, and the same list can be reused as the reader
 * steps through it.
 *
 * It is found by its address (`currentPath`), and only by it: a record that no row is at is not a
 * member of the list, and has no step (I-1304). Its key is asked only of a caller that gives no
 * address, and only while it names a single row: under All work areas two work areas may each have
 * a `D-0003` in the one list, and a key that names two rows identifies neither.
 */
export function useOriginatingListStep(
  currentKey: string | null | undefined,
  currentPath?: string,
): IOriginatingListStep | null {
  const list = useOriginatingList();
  if (!list || !currentKey) return null;
  const here = currentPath === undefined ? null : pathOf(currentPath);
  let at = here === null ? -1 : list.entries.findIndex((entry) => pathOf(entry.href) === here);
  if (here === null) {
    // No address to go by, so by key, and only where the key names one row: across work areas a
    // key repeats (two D-0001s under All work areas), and "the first D-0001" is then a guess,
    // which stepped to itself (I-1264).
    const sameKey = list.entries.filter((entry) => entry.key === currentKey);
    if (sameKey.length === 1) at = list.entries.indexOf(sameKey[0]);
  }
  // I-1304: with an address, a record no row is at is not a member of the list, whatever its key.
  // Reached by a link in text — another work area's I-0007 from a list that has its own — it once
  // matched that row by key, drew the arrows, and stepped a list it was never in.
  if (at === -1) return null;
  return { prev: list.entries[at - 1] ?? null, next: list.entries[at + 1] ?? null };
}
